// Every reducer case that is about a *run*.
//
// Stages, levels, cards, powers and the development tools. All of it
// is single-player: `server/rooms.ts` refuses this whole set (`SOLO_COMMANDS`),
// because each case here conjures pieces, ground, material, movement or
// invulnerability out of something a server has no reason to believe in.
//
// Split out of `commands.ts` for the reason its own PLAN §12.6 gives — see
// `industry/reduce.ts`, which was split at the same time and for the same
// reason.

// The shared machinery every reducer case is built out of.
//
// Split out of `commands.ts`, which had reached two thousand lines and thirty
// cases with **nine planned phases queued to add more**. The plan's own §12.6
// names it the contention point and recommended exactly this split; a file
// every feature has to edit is a file every feature conflicts in.
//
// Arranged so that nothing here imports a *value* from `commands.ts`. The
// case modules take their types from there with `import type`, which is
// erased at build time, and their helpers from here — that is the whole of
// why there is no runtime import cycle, and it is worth keeping. A cycle
// between a reducer and its helpers works right up until module evaluation
// order changes, and then fails as an undefined function at startup.

// The command log is the save format, the undo mechanism, the replay
// mechanism, the telemetry substrate and (from Phase 4) the network protocol.
// Every state change goes through `applyCommand` — nothing may mutate
// GameState directly.




import { waveDue, waveIn } from "./pending";
import { despairSlot, despairTurnsLeft } from "./despair";
import {
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_WK,
  CASTLE_WQ,
  FORWARD_FACING,
  KING,
  PAWN,
  type Direction,
  pieceColor,
  pieceType,
  WHITE
} from "../board";
import { holdsGround } from "../industry/belts";
import {
  claimStock,
  isBare,
  newStructure,
  reservedSquares,
  standStructure
} from "../industry/structures";
import { spendTicks } from "./ticks";
import { refuseBridge } from "./bridges";
import {
  clearBuffer,
  dropOnGround,
  hasStructure,
  makeUnit,
  setHeap,
  setSacrificedAt,
  unitAt,
  unitCount,
  type StructureKind,
  structureAt,
  isArtifactKind,
  clearDeposit,
  deliverUnit,
  pushUnit,
  takeUnitOf,
  unitMeets,
  unitValue,
  setGrowth
} from "../industry/state";
import {
  DESPAIR_IMMUNE,
  DESPAIR_NONE,
  FACTIONS,
  growSides,
  hasCell,
  obstructsAt,
  isOurs,
  livingSides,
  KING_FACTORY,
  pieceAt,
  setKingKind,
  setCells,
  setFacing,
  setPiece,
  setPromotionUses,
  removePiece,
  setVitality,
  addPlating
} from "../position";
import {
  RULES
} from "../rules";
import { depositFault, METAL } from "../materials";
import { CARDS, CARD_TABLE} from "./cards";
import { POWERS } from "./powers";
import { canSacrifice, sacrificeFurnace, sacrificeYield, type SacrificeError } from "./sacrifice";
import { runIsWon } from "./objectives";
import { removeSquare } from "../removeSquare";
import { growRegionZones, growZones, STAGE_ZONE_STREAM, ZONE_ROWS_MAX } from "../mapgen";
import { squaresBeyondEdge, squaresOnEdge } from "../boardEdges";
import { addCard} from "./deck";
import { applyGiveEquipment } from "./equipment";
import { canPayArtifact, payArtifact, uniquePlaceError } from "./artifacts";
import { applySeize } from "./scepter";
import { applyFuse } from "./fusion";
import {
  runGambit,
} from "../tick";
import { addTally, EMPTY_TALLY } from "../tally";
import type { Command, GameState} from "../commands";
import {
  IllegalCommandError,
  cloneState,
  requireOwnTurn,
  handClockTo,
  livingSideAfter,
  openRegion,
  placeDeposits,
  settledStatus,
  surrenderArmy,
  requireKingSafe
} from "../reduce";
import { endTurn, settleAction, spendMove } from "../clock";
/**
 * What the reducer says for each way a sacrifice is refused.
 *
 * Its own table rather than strings inside the case, because the rule moved to
 * `campaign/sacrifice.ts` and the *wording* is this layer's — the shelf phrases
 * the same refusals for a player, exactly as `UPGRADE_REFUSAL` does beside
 * `canUpgrade`.
 */
const SACRIFICE_REFUSAL: Record<SacrificeError, string> = {
  "not-your-turn": "not your turn",
  "no-power": "you do not have Sacrifice",
  "in-check": "cannot sacrifice while in check",
  "no-king": "no king",
  "already-this-turn": "already sacrificed this turn",
  "not-your-piece": "not your piece",
  "cannot-be-taken": "that piece cannot be taken apart",
  "not-beside-your-altar": "it has to be standing beside your altar or your Kris-bearer"
};

export function reduceCampaign(state: GameState, command: Command): GameState | null {
  switch (command.type) {
    case "clear-front": {
      // The same three guards `next-stage` carries, in the same order and for
      // the same reason: this destroys an army, so it is honest here rather
      // than by the grace of whoever sent it. What it does *not* do is conjure
      // one, which is why there is no camp to validate and no `runIsWon` check
      // — a won run simply never reaches this, because nothing asks for a
      // front to close after the launch.
      if (state.status !== "checkmate" && state.status !== "stalemate") {
        throw new IllegalCommandError("a stage only turns over when a side cannot move");
      }
      if (state.position.turn !== command.side) {
        throw new IllegalCommandError("only the beaten side is replaced");
      }
      if (state.run && isOurs(state.position, command.side, state.run.side)) {
        throw new IllegalCommandError("a friendly army being beaten is not a cleared stage");
      }
      // **A run is what a pending camp is kept on**, so there is nowhere to
      // record the wait without one. A scenario clears fronts too and takes
      // waves exactly as a run does; it simply takes them at once.
      if (!state.run) throw new IllegalCommandError("only a run can hold a front open");
      // At least one turn, or this is the undelayed boundary with an extra
      // command in front of it — two ways to do one thing, and the second one
      // reachable only by asking for a delay of nought.
      if (!Number.isInteger(command.delay) || command.delay < 1) {
        throw new IllegalCommandError("a wait is a whole number of turns");
      }

      const next = cloneState(state);
      // The same body a stage boundary uses: scrap where it stood, cargo
      // spilled, a roll off the loot table. One implementation of "what a dead
      // army leaves" — this is the *only* place that sweep happens for a
      // delayed front, and `next-stage` skips it when the camp finally lands.
      surrenderArmy(next, command.side);

      // **This is what unfreezes the board**, and it is the whole point of the
      // command. The beaten side is on the clock with no move, which reads as
      // mate; hand the clock to somebody who still exists and the status goes
      // back to `playing`, so the player can spend the turns they were sold.
      handClockTo(next.position, livingSideAfter(next.position, command.side), next.industry);
      next.run = {
        ...next.run!,
        pending: { until: state.position.round + command.delay }
      };
      next.status = settledStatus(next);
      return next;
    }

    case "next-stage": {
      // The gate: a stage only advances when the side being replaced has
      // genuinely run out of moves. Without it this is a command that
      // fabricates an army, and the reason the server refuses it would be the
      // only thing keeping it honest.
      //
      // Stalemate counts as well as mate. A side that cannot move has been
      // beaten in this game whether or not its king is attacked — and with
      // miners pinned to their heaps, "every piece I have left is down a mine"
      // is a real way to reach it.
      // **A camp already bought lands on its clock, not on a mate.** Its front
      // was closed turns ago by `clear-front` — the army is swept, the board is
      // playing, and the side on the clock is whoever is still alive — so all
      // three guards below would refuse it, and `dispatch` swallows a refusal:
      // the camp would simply never arrive and nothing would say why. The
      // spawn clock (§26 F10) is the second door through the same exemption: `waveDue` is the same subtraction
      // `stageTurnsOver` reads, so the gate and the driver cannot disagree.
      //
      // **A beaten board outranks every clock.** Under the spawn clock a mate
      // can coincide with a camp being due, and taken as on-time the landing
      // would skip the sweep below — the beaten army would stay standing with
      // no moves, half a front forever. A board in mate is a front bought the
      // ordinary way and takes the ordinary path, which is also what keeps
      // "beaten early still lands the next camp" B's rule under the clock.
      const beaten = state.status === "checkmate" || state.status === "stalemate";
      const onTime =
        !beaten &&
        (waveIn(state) === 0 || waveDue(state));

      if (!onTime) {
        if (state.status !== "checkmate" && state.status !== "stalemate") {
          throw new IllegalCommandError("a stage only turns over when a side cannot move");
        }
        if (state.position.turn !== command.side) {
          throw new IllegalCommandError("only the beaten side is replaced");
        }
      }
      // Never one of ours. An allied army
      // that gets itself mated must not turn the stage over: growing the board
      // as a reward for losing it is the opposite of a stage boundary.
      if (!onTime && state.run && isOurs(state.position, command.side, state.run.side)) {
        throw new IllegalCommandError("a friendly army being beaten is not a cleared stage");
      }
      // **The authoritative half of "a finished run gets no more camps".** The
      // driver checks too, but this is a command that conjures an army and
      // ground out of nothing, so the refusal belongs where every other thing
      // it must not do is refused.
      if (runIsWon(state)) {
        throw new IllegalCommandError("this run is over");
      }
      // **And a camp is never one of ours.** The driver picks the arriving
      // colour from a counter (`arrivingSide`), and the counter used to know
      // nothing about which sides existed or whose they were: an ally
      // takes the next free index, which in an ordinary run is 2, and stage
      // one wanted exactly 2. The camp landed *allied to the player* — no
      // enemies left on the board, no front able to turn over again — and its
      // king overwrote the allied king's slot.
      if (state.run) {
        for (const [, piece] of command.pieces) {
          if (isOurs(state.position, pieceColor(piece), state.run.side)) {
            throw new IllegalCommandError("a camp may not arrive under one of our own flags");
          }
        }
      }

      const next = cloneState(state);

      // The board opens first, so the wave has ground to land on. New squares
      // only ever add: territory once revealed stays revealed, and a piece
      // standing where the board vanished would be a much worse bug than a
      // board that is larger than it needs to be.
      // **The camp has landed, so it is no longer owed.** Left set, every
      // subsequent turn reads as a wave being due: `stageTurnsOver` answers
      // true on a playing board and the driver lands another camp, and another,
      // for the rest of the run.
      if (next.run?.pending) next.run = { ...next.run, pending: undefined };
      // And the spawn clock is spent the same way (§26 F10): the landing
      // consumes it, and the next `endTurn` re-arms it — so the interval runs
      // landing to landing, and a front beaten early resets it rather than
      // leaving the schedule owed from before the fight.
      if (next.run?.spawn) next.run = { ...next.run, spawn: undefined };

      // Ground coming back: a seed and a counter, and the tick does the rest.
      if (command.grow) {
        setGrowth(next.industry, command.grow.seed, command.grow.depth);
      }

      // **Buildings before ground**, so the cell filter below takes their
      // squares out — the same order `openRegion` runs in and for the same
      // reason: a square that is both built on and walkable is the state every
      // "no piece may occupy one" rule quietly stops being true in.
      for (const [sq, kind, owner] of command.structures ?? []) {
        if (hasStructure(next.industry, sq) || pieceAt(next.position, sq) !== 0) continue;
        // Through `standStructure` for the mirror — see `openRegion` (§26 F5).
        standStructure(next.position, next.industry, sq, newStructure(kind as StructureKind, owner));
      }

      if (command.cells && command.cells.length > 0) {
        // Never over a building — see `openRegion` for why a square that is
        // both ground and a warehouse breaks the encoding.
        setCells(next.position, [
          ...next.position.cells,
          ...command.cells.filter((sq) => !hasStructure(next.industry, sq))
        ]);
      }
      if (command.sides) growSides(next.position, command.sides);
      // **The board grows by zones** (§31 D3): after the camp's ground, so a
      // zone only ever lays new ground — never on the camp's or the player's.
      // Indexed by the stage, so no two stages grow off one stream.
      if (command.zones?.length) {
        growZones(
          next.position,
          next.industry,
          command.zones,
          next.seed,
          STAGE_ZONE_STREAM,
          (state.stage ?? 0) * ZONE_ROWS_MAX
        );
      }

      // The beaten army becomes scrap where it stood, holdings and all — the
      // same thing dying does anywhere else, and the reward for the stage.
      //
      // **Nobody is beaten when a camp lands on its clock**, and
      // `command.side` is then the side *holding* the clock — very likely the
      // player. Sweeping it would hand the run's own army to the scrap heap
      // as the reward for surviving.
      if (!onTime) surrenderArmy(next, command.side);

      // Before the pieces, so a formation that happens to stand on one is
      // standing on ground that is already special rather than quietly
      // overwriting it.
      for (const [sq, uses] of command.promotion ?? []) {
        if (!hasCell(next.position, sq)) continue;
        setPromotionUses(next.position, sq, uses);
      }

      for (const [sq, piece] of command.pieces) {
        if (!hasCell(next.position, sq) || pieceAt(next.position, sq) !== 0) continue;
        if (obstructsAt(next.position, sq)) continue;
        setPiece(next.position, sq, piece);
        // A pawn with no facing set moves the wrong way; parseFen does this
        // for a normal game and there is no FEN here.
        setFacing(next.position, sq, pieceType(piece) === PAWN ? FORWARD_FACING[pieceColor(piece)] : 0);
        // Under its *own* flag, not the beaten side's. A wave can arrive as
        // an army that was not on the board, and filing its king under the
        // side it replaced leaves that king unfindable — so it can never be
        // checked, and the stage it belongs to can never be cleared.
        if (pieceType(piece) === KING) {
          next.position.kings[pieceColor(piece)] = sq;
          // A factory army, the only faction (§31 R2). Set rather than left to
          // `growSides`: the side a wave flies cycles back round to one that
          // was at the opening.
          setKingKind(next.position, pieceColor(piece), KING_FACTORY);
        }
      }

      // The faction each arriving side flies (§31 G3), after the pieces'
      // factory default.
      for (const [side, kind] of command.kings ?? []) {
        if (!FACTIONS.includes(kind)) throw new IllegalCommandError(`unknown faction: ${kind}`);
        setKingKind(next.position, side, kind);
      }

      // Facing markers override the arriving side's forward, and run *after*
      // the pieces for the reason `openRegion` does it in that order: the
      // pieces set a default facing as they land, so a marker read first is a
      // marker immediately overwritten.
      for (const [sq, direction] of command.facing ?? []) {
        if (pieceType(pieceAt(next.position, sq)) === PAWN) setFacing(next.position, sq, direction as Direction);
      }
      for (const [sq, amount] of command.heaps ?? []) {
        if (hasCell(next.position, sq)) setHeap(next.industry, sq, amount);
      }
      for (const [sq, grade, count] of command.metal ?? []) {
        if (!hasCell(next.position, sq)) continue;
        for (let i = 0; i < count; i++) {
          dropOnGround(next.industry, sq, makeUnit(grade as 0 | 1 | 2 | 3));
        }
      }
      // After the pieces and the heaps, so a seam under an arriving knight or
      // under the camp's own scrap is skipped rather than overwriting it.
      placeDeposits(next.position, next.industry, command.deposits ?? []);

      // The Gambit runs first, on the board as the last fight left it — the
      // arriving army has no factory yet, so running it afterwards would hand
      // a share of the work to a camp that did nothing to earn it.
      if (command.gambit) {
        next.lastGambit = runGambit(next.position, next.industry);
      }

      // Hand the clock to an army that still exists.
      //
      // The beaten side has just been swept off the board, and a wave can
      // arrive under a *different* flag — so leaving the turn where it was
      // leaves a side with no pieces on the clock. That side has no moves, so
      // it reads as mated, so another stage turns over, and the board grows
      // forever without anyone playing a move.
      // The player moves first against a new camp. An army that arrives and
      // immediately plays has effectively moved twice — once by materialising
      // in formation, once by acting on it — and the stage before it ended
      // with the player earning a breath.
      //
      // **Only when somebody was beaten.** An on-time landing arrives on a
      // *playing* board — `command.side` is whoever happens to hold the clock,
      // very likely the player mid-turn — so handing the clock "on" from them
      // steals the rest of their turn and gives it to an army that did
      // nothing, and the castling strip below would take the *player's* own
      // rights as the price of a fight nobody lost. The beaten-side
      // bookkeeping belongs to a beaten side (§26 F10).
      if (!onTime) {
        handClockTo(
          next.position,
          livingSideAfter(
            next.position,
            command.side,
            new Set(command.pieces.map(([, piece]) => pieceColor(piece)))
          ),
          next.industry
        );

        // The new force has never moved, and the position it arrives in has
        // nothing to do with the one that repeated before it.
        next.position.castling &=
          command.side === WHITE ? CASTLE_BK | CASTLE_BQ : CASTLE_WK | CASTLE_WQ;
        next.position.ep = -1;
      }
      // A landing is irreversible whichever door it arrived through — the
      // board has grown, so no earlier position can recur.
      next.position.halfmove = 0;
      next.repetitions = new Map();

      // The scorecard closes here and a fresh one opens. Read by the round
      // card, which fires on this command being the last one in the log — so
      // the two agree by construction, and a stage's numbers are the stage's
      // rather than the run's running total.
      next.lastStage = next.tally ?? EMPTY_TALLY;
      // The run's running total, closed fronts only. One site, at the one
      // moment a front actually finishes, so it cannot drift from the per-front
      // count it is made of.
      next.runTally = addTally(next.runTally ?? EMPTY_TALLY, next.lastStage);
      next.tally = EMPTY_TALLY;
      // And the round itself, for the same reason and in the same place. It
      // was counted off the log — which a run is not saved as — so a reloaded
      // run came back at stage one and drew stage one's camp at stage one's
      // difficulty. See `GameState.stage`.
      next.stage = (state.stage ?? 0) + 1;

      next.status = settledStatus(next);
      return next;
    }

    case "set-despair": {
      const side = command.side;
      if (side < 0 || side >= state.position.sides) {
        throw new IllegalCommandError("no such side");
      }
      const was = state.position.despair[side] ?? DESPAIR_NONE;
      // **Immunity is sticky and outranks everything.** An army exempted for
      // arriving unable to win must not be re-diagnosed by the next turn's
      // check, or the exemption lasts exactly one turn and the squad still
      // surrenders — three turns later, which is the bug this is for.
      if (was === DESPAIR_IMMUNE) return state;
      if (command.immune) {
        const next = cloneState(state);
        next.position.despair[side] = DESPAIR_IMMUNE;
        return next;
      }
      // A turn slot, not ticks (§31 T): the grace counts the side's own turns.
      const now = command.depressed ? despairSlot(state.position) : DESPAIR_NONE;
      // **Idempotent.** Re-stamping a side that is already depressed must not
      // restart its three turns — the check runs every turn, so an army would
      // be reprieved by the very thing that condemned it. Clearing one that is
      // not depressed is a no-op for the same reason: nothing to say.
      if (command.depressed === was >= 0) return state;

      const next = cloneState(state);
      next.position.despair[side] = now;
      return next;
    }

    case "surrender": {
      const side = command.side;
      const at = state.position.despair[side] ?? DESPAIR_NONE;
      // Gated here, not only at the server. This turns a whole army into scrap
      // — the same thing `next-stage` does, and gated for the same reason: a
      // command that destroys an army should not be honest only by the grace
      // of whoever sends it.
      if (at < 0) throw new IllegalCommandError("that army has not despaired");
      // Hard Mode's grace period is nought (§26 F13), so a stamp that landed
      // this turn is already old enough — age 0 is not less than 0, which is
      // "no grace period" arriving through the arithmetic rather than through
      // a branch.
      if ((despairTurnsLeft(state, side) ?? 0) > 0) {
        throw new IllegalCommandError("it has not been given its turns yet");
      }
      if (!livingSides(state.position).includes(side)) {
        throw new IllegalCommandError("that army is already gone");
      }

      const next = cloneState(state);
      // The same body a stage boundary uses: scrap where it stood, cargo
      // spilled, a roll off the loot table. One implementation of "what a dead
      // army leaves", or the two paths pay differently for the same pieces.
      surrenderArmy(next, side);
      next.position.despair[side] = DESPAIR_NONE;

      // **An enemy camp that gives up is a beaten army** (§26 F9): the clock
      // is left *on* the swept side, where a side with no pieces reads as
      // checkmate, and the ordinary stage machinery takes it from there —
      // `stageTurnsOver` answers true, the tally rolls over, the next camp
      // lands, and the landing hands the clock on exactly as a mate's does.
      // The "grows the board forever" failure the other branch guards against
      // needs the clock to *stay* on the empty side after a landing, and the
      // landing is what moves it.
      //
      // A side with a living ally that can move is passed over by
      // `settledStatus` (`idleStuckSides`), which is also correct here: a
      // surrendering ally whose teammate fights on has not ended the front,
      // it has simply left it.
      if (next.run && !isOurs(next.position, side, next.run.side)) {
        handClockTo(next.position, side, next.industry);
        next.status = settledStatus(next);
        return next;
      }

      // The clock has to land on an army that still exists — a side with no
      // pieces reads as mated, which in single player turns a stage over every
      // turn and grows the board forever with nobody playing. Right for the
      // run's own side and its allies (a surrendered run is its own ending,
      // `defeatReason`'s, not a cleared front), and for every game with no
      // run, where nothing is waiting to land a camp on a mate-read.
      handClockTo(next.position, livingSideAfter(next.position, side), next.industry);
      next.status = settledStatus(next);
      return next;
    }

    case "dev-grow": {
      const cells = [
        ...(command.cells ?? []),
        ...(command.edge ? squaresBeyondEdge(state.position, command.edge) : [])
      ];
      const next = cloneState(state);
      // A building's square is already a cell (`standStructure`).
      setCells(next.position, [
        ...next.position.cells,
        ...cells.filter((sq) => !hasStructure(next.industry, sq))
      ]);

      for (const [sq, piece] of command.pieces ?? []) {
        if (!hasCell(next.position, sq) || pieceAt(next.position, sq) !== 0) continue;
        // Nobody stands on an obstruction (§31 S1).
        if (obstructsAt(next.position, sq)) continue;
        growSides(next.position, pieceColor(piece) + 1);
        setPiece(next.position, sq, piece);
        // A pawn with no facing moves the wrong way, and there is no FEN here.
        setFacing(
          next.position,
          sq,
          pieceType(piece) === PAWN ? FORWARD_FACING[pieceColor(piece)] : 0
        );
        // Filed under its own flag — a king filed under another side is
        // unfindable.
        if (pieceType(piece) === KING) {
          next.position.kings[pieceColor(piece)] = sq;
          setKingKind(next.position, pieceColor(piece), KING_FACTORY);
        }
        // **A tough army** (§27): every piece it stands up carries the same
        // health, whatever its rank. That is the point of the override — a
        // body of pawns on `PIECE_HEALTH` falls to anything that touches it,
        // and what the mechanic is for is an army you have to grind down.
        if (command.tough) setVitality(next.position, sq, command.tough);
        // **A plated army**: `tough` one layer up, for what the emplacements throw.
        if (command.plated) addPlating(next.position, sq, command.plated);
      }

      for (const [sq, amount] of command.heaps ?? []) {
        if (hasCell(next.position, sq)) setHeap(next.industry, sq, amount);
      }

      for (const [sq, kind, owner] of command.structures ?? []) {
        if (!hasCell(next.position, sq) || pieceAt(next.position, sq) !== 0) continue;
        if (hasStructure(next.industry, sq)) continue;
        growSides(next.position, owner + 1);
        clearDeposit(next.industry, sq);
        // Through `standStructure` for the mirror, and concrete buries the
        // seam — as `place-structure` does.
        standStructure(next.position, next.industry, sq, newStructure(kind as StructureKind, owner));
      }
      return next;
    }

    case "dev-collapse": {
      const cells = [
        ...(command.cells ?? []),
        ...(command.edge ? squaresOnEdge(state.position, command.edge) : [])
      ];
      // A king's square is refused outright: a board with its king gone is
      // one no rule here knows how to read.
      for (const sq of cells) {
        if (pieceType(pieceAt(state.position, sq)) === KING) {
          throw new IllegalCommandError("a king is standing on that ground");
        }
      }
      const next = cloneState(state);
      // The Micro-Singularity Bomb's own removal.
      for (const sq of cells) {
        if (hasCell(next.position, sq)) removeSquare(next.position, next.industry, sq);
      }
      next.status = settledStatus(next);
      return next;
    }

    case "dev-defeat": {
      const side = command.side;
      const living = livingSides(state.position);
      if (!living.includes(side)) throw new IllegalCommandError("that army is already gone");
      // The clock has to land on somebody. Beating the last army leaves a board
      // with nobody on it, which reads as mated, which in single player turns a
      // stage over every turn and grows the board forever — the same failure
      // `livingSideAfter` exists for, one step earlier.
      if (living.length < 2) throw new IllegalCommandError("that is the last army playing");

      const next = cloneState(state);
      // The same body a stage boundary and a surrender use. A dev tool that
      // cleared a camp *differently* would be a board no player can reach.
      surrenderArmy(next, side);
      handClockTo(next.position, livingSideAfter(next.position, side), next.industry);
      next.status = settledStatus(next);
      return next;
    }

    case "dev-material": {
      const { square, grade, delta } = command;
      // **Absent is metal** (§26 F2's rule), so every log written before lines
      // existed replays identically.
      const line = command.line ?? METAL;
      // Refused, never defaulted — `normalizeRuleset`'s rule. A rung no
      // ladder has (metal 3) would mint a unit word no play can produce, and every reader from the
      // shelf to the recipes would be handed a material with no name.
      const fault = depositFault(line, grade);
      if (fault) throw new IllegalCommandError(fault);
      const structure = structureAt(state.industry, square);
      if (!structure && pieceAt(state.position, square) === 0) {
        throw new IllegalCommandError("nothing there to hold material");
      }
      if (delta === 0) return state;

      const next = cloneState(state);
      if (delta > 0) {
        // Through `deliverUnit`, which is the one place that knows where
        // material lands — a piece's inbox or a building's stock. Four
        // hand-off sites once pushed units by hand and silently dropped
        // everything handed to a store; a dev tool doing the same would put a
        // board in front of somebody that no play can produce.
        for (let i = 0; i < delta; i++) {
          deliverUnit(next.position, next.industry, square, makeUnit(grade, 0, line));
        }
      } else {
        // Cheapest first *within the named line*, exactly as a bill row is
        // paid (`unitMeets` converts down within a line, never across one) —
        // so a decrement on the stone row cannot quietly spend the iron.
        for (let i = 0; i < -delta; i++) {
          if (structure) {
            const stock = structureAt(next.industry, square)!.stock;
            let at = -1;
            for (let j = 0; j < stock.length; j++) {
              if (!unitMeets(stock[j], line, grade)) continue;
              if (at < 0 || unitValue(stock[j]) < unitValue(stock[at])) at = j;
            }
            if (at < 0) break;
            stock.splice(at, 1);
          } else if (takeUnitOf(next.industry, square, line, grade) < 0) {
            // Nothing left in the inbox — try the out bank, or a refinery's
            // output would be the one material a dev cannot clear.
            //
            // **`< 0`, never truthiness.** `takeUnitOf` answers -1 for
            // "nothing to take" and the *unit word* otherwise — so `-1` is
            // truthy and a grade-0 unit is `0`, which is falsy: the obvious
            // test is wrong in both directions at once, reading a failure as
            // a success and a taken bar of iron as a failure that then takes
            // a second one out of the other bank. The same "a unit is a
            // packed word with no id" trap the turret's upgrade pass paid for.
            if (takeUnitOf(next.industry, square, line, grade, "out") < 0) break;
          }
        }
      }
      return next;
    }

    case "reveal-region": {
      const run = state.run;
      const level = run?.level;
      if (!run || !level) throw new IllegalCommandError("not a run");
      if (run.revealed.includes(command.region)) {
        throw new IllegalCommandError("that region is already open");
      }
      const region = level.regions.find((entry) => entry.id === command.region);
      if (!region) throw new IllegalCommandError("no such region");

      // Gated on the objective, exactly as `next-stage` is gated on a side
      // having no move. The server refusing it is not the only guard, because
      // a run is local and there is no server.
      const opener = run.objectives.find((entry) => entry.id === region.revealedBy);
      if (region.revealedBy === "" || !opener?.done) {
        throw new IllegalCommandError("that region has not been earned");
      }

      const next = cloneState(state);
      // The army first, so its ground is clear before the new region lands and
      // so its crown is on the board when the camera arrives.
      if (command.defeated !== undefined) surrenderArmy(next, command.defeated);
      // The room's own zones (§31 D3) before its tiles, as at the opening.
      growRegionZones(
        next.position,
        next.industry,
        region,
        next.seed,
        level.regions.findIndex((entry) => entry.id === region.id)
      );
      openRegion(next, region);

      // Then the lobe a field grew for itself, through the same `setCells`
      // every other expansion uses — never a second way to add ground. Never
      // over a building, for `openRegion`'s own reason.
      if (command.cells && command.cells.length > 0) {
        setCells(next.position, [
          ...next.position.cells,
          ...command.cells.filter((sq) => !hasStructure(next.industry, sq))
        ]);
      }
      // And the field last, after the ground exists and after the region's own
      // pieces and seam have landed on it — `placeDeposits` skips a tile that
      // is taken, so the order is what decides which of them wins.
      placeDeposits(next.position, next.industry, command.deposits ?? []);

      if (region.gambit) {
        next.lastGambit = runGambit(next.position, next.industry, [run.side]);
        next.lastTick = next.lastGambit;
      }

      next.run = { ...run, revealed: [...run.revealed, region.id] };

      // The clock has to land on an army that still exists: sweeping one and
      // leaving the turn on it is a side with no moves, which reads as mated.
      handClockTo(
        next.position,
        livingSideAfter(
          next.position,
          next.position.turn,
          new Set(region.pieces.map(([, piece]) => pieceColor(piece)))
        ),
        next.industry
      );
      next.status = settledStatus(next);
      return next;
    }

    case "pave": {
      const owner = requireOwnTurn(state);
      const run = state.run;
      if (!run) throw new IllegalCommandError("not a run");
      const laid = command.squares;
      if (!run.tiles || run.tiles < laid.length) {
        throw new IllegalCommandError("no board tiles left");
      }
      // Every rule about the chain, shared with the board's highlight.
      const why = refuseBridge(state.position, state.industry, laid);
      if (why) throw new IllegalCommandError(why);

      const next = cloneState(state);
      // `run` is shared by reference across a clone, so a command that writes
      // to it has to take a fresh one or it edits the previous state's — the
      // same trap `play-card` is written around.
      next.run = { ...run, tiles: run.tiles - laid.length };
      setCells(next.position, [...next.position.cellList, ...laid]);

      // **The half taking ground away never needed.** Deleting a cell can only
      // shorten a ray, so that can break a check and never give one. Paving
      // lengthens rays, so a tile can open a line straight onto your own king
      // — checked after the fact rather than predicted, because "which rays
      // does this square join" is the question the attack sweep already
      // answers.
      requireKingSafe(next.position, owner, "pave");
      return next;
    }

    case "sacrifice": {
      const owner = requireOwnTurn(state);
      // **One predicate, asked here and by the shelf that draws the control.**
      // This case held the whole rule inline and answered it one square at a
      // time, so the interface could only offer the power by restating six
      // conditions beside it — and a second copy of "may this be taken apart"
      // is how a button comes to be drawn live over a square this then
      // refuses. See `campaign/sacrifice.ts`.
      const why = canSacrifice(state, owner, command.square);
      if (why) throw new IllegalCommandError(SACRIFICE_REFUSAL[why]);

      // `canSacrifice` has already established both of these exist.
      const king = state.position.kings[owner]!;
      const piece = pieceAt(state.position, command.square)!;

      const next = cloneState(state);
      const type = pieceType(piece);
      // **Half the bill, rounded up, at the bill's grades** (§31 A3) — by the
      // piece's current type, never by what it was minted as. Provenance
      // would be new state on every piece and would make two identical
      // queens worth different amounts.
      const yielded = sacrificeYield(type);
      // What it was carrying comes through at its own grade, on top.
      for (const side of ["in", "out"] as const) {
        for (let i = 0; i < unitCount(next.industry, command.square, side); i++) {
          yielded.push(unitAt(next.industry, command.square, i, side));
        }
      }
      clearBuffer(next.industry, command.square);
      removePiece(next.position, command.square);

      // **Which furnace takes it** (§31 A3), through the same
      // `sacrificeFurnace` the predicate asked: the altar's stock (born
      // offering, so `runStores` carries it off) or the Kris bearer's
      // inventory. What will not fit is **set down, not scrapped** — on
      // `dropped`, where anyone walking onto the square picks it up.
      const furnace = sacrificeFurnace(state, owner, command.square)!;
      for (const unit of yielded) {
        const kept =
          furnace.kind === "altar"
            ? claimStock(structureAt(next.industry, furnace.at)!, unit)
            : pushUnit(next.industry, furnace.at, unit, "in");
        if (!kept) dropOnGround(next.industry, command.square, unit);
      }

      // A sacrificed piece may have been the only thing blocking a line.
      requireKingSafe(next.position, owner, "sacrifice");
      // **One bonus factory tick, by either furnace** (§31 A3), through
      // `spendTicks` — the one door a bought tick has (C2's rule), so it
      // credits the tally, advances `turnCounter` and carries no pieces.
      spendTicks(next, owner, 1);
      // **It costs the turn, exactly as a forge does.** See the note at the
      // top of `campaign/powers.ts`: a power that changes the board for free is
      // a decision with no price, and this one turns a body into metal — a
      // trade every player would take every turn if it cost nothing.
      //
      // The once-a-turn stamp above survives it rather than being made
      // redundant: `movesPerTurn` above one hands a side several moves before
      // the clock passes, so "the turn is spent" and "this may not happen
      // twice this turn" stop being the same sentence.
      next.position.halfmove++;
      spendMove(state, next, owner, false);

      // **Stamped with the round** (§31 T): one turn per side per round, and
      // the clock holds through the turn's moves, so Industrial Titan's second
      // move still finds this turn's stamp. Not `turnCounter`, which the
      // move's tick advances.
      setSacrificedAt(next.industry, king, next.position.round);
      return next;
    }

    case "set-power": {
      const run = state.run;
      if (!run) throw new IllegalCommandError("not a run");
      if (!POWERS.includes(command.power)) throw new IllegalCommandError("no such power");
      // **Every power is placed now** (§26 A2, finishing F6). The structure
      // powers were always on while their structure stands, and Titan's count
      // was the holdout — `run.titan`, behind this toggle — until A2 derived
      // it from Relics standing. A membership flag would be a second answer to
      // a question the board owns, so the dev door for all four is
      // `grant-token` + `place-structure`. The case survives for the log
      // format, `observe-wave`'s shape: it refuses every play.
      throw new IllegalCommandError("powers are placed, not set");
    }

    case "grant-token": {
      // Dev: `grant-card`'s shape. Into the run's hand of tokens — the one
      // way an artifact structure reaches a run until A1 (§31 R1).
      const run = state.run;
      if (!run) throw new IllegalCommandError("not a run");
      if (!isArtifactKind(command.kind)) {
        throw new IllegalCommandError("not an artifact structure");
      }
      const next = cloneState(state);
      next.run = { ...run, tokens: [...(run.tokens ?? []), command.kind] };
      return next;
    }

    case "place-structure": {
      // Stand an artifact structure up from a token (§26 F6). No foundry and
      // no adjacency — the ground rule is bare ground you hold — but since
      // §31 A3–A5 it pays the artifact's bill (`campaign/artifacts.ts`).
      const owner = requireOwnTurn(state);
      const run = state.run;
      if (!run) throw new IllegalCommandError("not a run");
      if (owner !== run.side) throw new IllegalCommandError("not your token");
      if (!isArtifactKind(command.kind)) {
        throw new IllegalCommandError("only artifact structures are placed from tokens");
      }
      const held = (run.tokens ?? []).indexOf(command.kind);
      if (held < 0) throw new IllegalCommandError("no such token in hand");
      // The Pillar of Excellence (§31 A6): one on the map, none once lost;
      // Stoke the Forges (A10): one per side.
      const unique = uniquePlaceError(state, command.kind, owner);
      if (unique) throw new IllegalCommandError(`cannot place: ${unique}`);
      const reserved = reservedSquares(state);
      if (!isBare(state.position, state.industry, command.at, reserved)) {
        throw new IllegalCommandError("cannot place: square not empty");
      }
      if (!holdsGround(state.position, state.industry, command.at, owner)) {
        throw new IllegalCommandError("cannot place: not your ground");
      }
      // **The bill** (§31 A3–A5): the equipped Monarch's inventory, then the
      // stores nearest the site. The token is the right to build, not the price.
      if (!canPayArtifact(state, command.kind, owner, command.at)) {
        throw new IllegalCommandError("cannot place: too poor");
      }

      const next = cloneState(state);
      payArtifact(next, command.kind, owner, command.at);
      // **Concrete buries the seam**, exactly as `buildStructure` does: paving
      // over ore is the player's call, clearing the orphaned tile is not.
      clearDeposit(next.industry, command.at);
      standStructure(
        next.position,
        next.industry,
        command.at,
        newStructure(command.kind, owner)
      );
      const tokens = [...(next.run!.tokens ?? [])];
      tokens.splice(held, 1);
      next.run = { ...next.run!, tokens: tokens.length > 0 ? tokens : undefined };
      // Spends the turn, so it may not leave your own king attacked — and a
      // structure takes its square off the board, which cuts lines, so it can
      // resolve a check too. The forge's rule, via build.
      requireKingSafe(next.position, owner, "place");
      // **Costs no move** (§31 T4): building a structure is factory work.
      settleAction(next, owner);
      next.lastTick = {
        moved: 0,
        mined: 0,
        cooked: 0,
        minted: [],
        placed: [{ square: command.at, kind: command.kind }]
      };
      return next;
    }

    case "grant-card": {
      const run = state.run;
      if (!run) throw new IllegalCommandError("not a run");
      if (!CARDS.includes(command.card)) throw new IllegalCommandError("no such card");

      const next = cloneState(state);
      // Into the deck *and* into the hand: a dev tool that made you wait a
      // turn to test the card you just asked for would be a dev tool nobody
      // uses. `addCard` mutates, so it gets a fresh run — `cloneState` shares
      // it, and every path here replaces rather than mutates.
      const fresh = { ...run, deck: [...run.deck], hand: [...run.hand] };
      const instance = addCard(fresh, command.card);
      fresh.hand = [...fresh.hand, instance.id].sort((a, b) => a - b);
      next.run = fresh;
      return next;
    }

    case "burn-card": {
      // **Deck, never hand, and free of the turn.** Both are the rewind's
      // doing: the hand at the point a rewind lands is whatever `dealHand`
      // sampled there, and the turn is the thing being handed back. Not
      // `requireOwnTurn` either — the board this arrives on is the start of the
      // player's own turn by construction, so a second test would only be a
      // second thing able to disagree with `planRewind`.
      const run = state.run;
      if (!run) throw new IllegalCommandError("not a run");
      const held = run.deck.find((entry) => entry.id === command.instance);
      if (!held) throw new IllegalCommandError("no such card");

      const next = cloneState(state);
      // The same three fields `play-card` leaves behind, and for the same
      // reasons: out of the deck for good (there is no discard pile), out of
      // the hand it may or may not have been in, and on to `spent`, which is
      // what the run reports as having been used.
      next.run = {
        ...run,
        deck: run.deck.filter((entry) => entry.id !== command.instance),
        hand: run.hand.filter((id) => id !== command.instance),
        spent: [...run.spent, held.card]
      };
      return next;
    }

    case "give-equipment":
      return applyGiveEquipment(state, command);

    case "seize":
      return applySeize(state, command);

    case "fuse":
      return applyFuse(state, command);

    case "claim-trophy":
    case "keep-trophy": {
      // §31 A2. Free, like a card, and bound to the run side's own turn.
      const owner = requireOwnTurn(state);
      const run = state.run;
      if (!run || run.side !== owner) throw new IllegalCommandError("not a run");
      const trophy = (run.trophies ?? []).find((one) => one.id === command.trophyId);
      if (!trophy) throw new IllegalCommandError("no such trophy");
      const rest = (run.trophies ?? []).filter((one) => one.id !== trophy.id);
      const next = cloneState(state);
      const fresh = {
        ...run,
        deck: [...run.deck],
        hand: [...run.hand],
        trophies: rest.length > 0 ? rest : undefined
      };
      if (command.type === "claim-trophy") {
        const card = trophy.offer[command.choice];
        if (!Number.isInteger(command.choice) || card === undefined) {
          throw new IllegalCommandError("that trophy offers no such card");
        }
        const instance = addCard(fresh, card);
        fresh.hand = [...fresh.hand, instance.id].sort((a, b) => a - b);
      } else {
        next.tally = addTally(next.tally, { trophies: 1, score: RULES.trophyScore });
      }
      next.run = fresh;
      return next;
    }

    case "play-card": {
      const owner = requireOwnTurn(state);
      const run = state.run;
      if (!run || run.side !== owner) throw new IllegalCommandError("not a run");
      if (!run.hand.includes(command.instance)) {
        throw new IllegalCommandError("that card is not in your hand");
      }
      const held = run.deck.find((entry) => entry.id === command.instance);
      if (!held) throw new IllegalCommandError("no such card");

      const card = CARD_TABLE[held.card];
      const next = cloneState(state);
      // A fresh run object *before* the card runs. `cloneState` shares `run`
      // by reference on the grounds that every path replaces it rather than
      // mutating it — and a card effect is handed the cloned state and may
      // well write to it — Bridges banks tiles,
      // and Load Em Up wrote a countdown before it became a rule. Without
      // this, playing the card reaches back and edits the previous state's run
      // as well, so an undo or a replay sees a card that was never played.
      next.run = { ...run };
      // **Every field `CardPlay` declares, or the card that needs the missing
      // one can never be played.** `source` was absent here while `CardPlay`
      // had it and Imitate required it, so that card's `refuse` answered
      // "needs-a-target" on every play forever — a whole card unreachable
      // through the reducer, and silent, because the refusal names the *play*
      // rather than the wiring.
      const play = {
        target: command.target,
        pieces: command.pieces,
        destination: command.destination,
        source: command.source,
        direction: command.direction
      };
      const refusal = card.refuse(next, play);
      if (refusal) throw new IllegalCommandError(refusal);

      card.apply(next, play);

      // Consumable: a played card leaves the deck for good (PLAN §12.3.2).
      // There is no discard pile and nothing is reshuffled, which is what
      // makes a small deck safe — with recycling, one Reactor Boosters is two
      // spare moves every turn for the rest of the run.
      // A card that costs the turn spends it exactly as a move does — same
      // clocks, same tick, same side change — rather than by a second path
      // that would have to be kept in step with `passTurn`.
      // `"turn"` gives up every move left in it, through the one body that
      // knows how — `forfeitTurn`, which the `pass` command also ends a turn
      // with. `true` is the ordinary one-move cost.
      if (card.spendsTurn === "turn") {
        endTurn(state, next, owner);
      } else if (card.spendsTurn) {
        next.position.halfmove++;
        spendMove(state, next, owner, false);
      }

      next.run = {
        // Spread what the card left behind, not the run it started from, or
        // any effect written into `run` is discarded on the next line.
        ...next.run,
        // A reusable card is played and kept. It still leaves your *hand* for
        // the turn — a card you can play twice on one turn is a different and
        // much stronger thing — and comes back with the next deal. This is
        // what the deck is for; without it every card is a one-shot and "deck"
        // just means "the cards you have not burned yet".
        deck: card.reusable
          ? run.deck
          : run.deck.filter((entry) => entry.id !== command.instance),
        hand: run.hand.filter((id) => id !== command.instance),
        spent: [...run.spent, held.card]
      };
      return next;
    }

    default:
      // Not one of ours. `applyCommand` asks each reducer in turn, so "null"
      // means "somebody else's case" rather than "unknown command" — the
      // exhaustiveness check that catches a genuinely unhandled type stays in
      // `applyCommand`, where the whole union is in scope.
      return null;
  }
}
